import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { SnackbarService } from "@claas/claas-ui-components/notification/snackbar";

/**
 * Centralizes user-facing error feedback. Wraps the CLAAS {@link SnackbarService}
 * so the rest of the app depends on a small, testable seam instead of the
 * component library directly, and so HTTP status codes are mapped to friendly,
 * non-technical messages in one place.
 */
@Injectable({ providedIn: "root" })
export class ErrorNotificationService {
  private readonly snackbar = inject(SnackbarService);

  /** Shows a generic, already user-friendly message. */
  notify(message: string): void {
    this.snackbar.show(message);
  }

  /** Maps an {@link HttpErrorResponse} to a friendly message and shows it. */
  notifyHttpError(error: HttpErrorResponse): void {
    this.snackbar.show(this.toMessage(error));
  }

  private toMessage(error: HttpErrorResponse): string {
    if (error.status === 0) {
      return "Network error — please check your connection and try again.";
    }
    if (error.status >= 500) {
      return "A server error occurred. Please try again later.";
    }
    if (error.status === 404) {
      return "The requested content could not be found.";
    }
    if (error.status === 401 || error.status === 403) {
      return "You are not authorized to perform this action.";
    }
    return "An unexpected error occurred. Please try again.";
  }
}
